<template>
    <canvas ref="canvasRef" class="canvas"></canvas>
</template>

<script setup>
    import { ref, onMounted, onUnmounted } from 'vue';
    import stores from '@/stores';

    const tgs = stores.tgs();
    const canvasRef = ref(null);

    let ctx = null;
    let animationFrame = null;
    let particles = [];
    let lastTime = 0;

    const mainLoop = (currentTime) => {
        if (!canvasRef.value) {
            return;
        }

        // Вычисляем дельту (1.0 для 60FPS)
        if (!lastTime) {
            lastTime = currentTime;
        }

        const deltaTime = Math.min((currentTime - lastTime) / (1000 / 60), 2.0);
        lastTime = currentTime;

        const w = canvasRef.value.width / tgs.dpr;
        const h = canvasRef.value.height / tgs.dpr;

        ctx.clearRect(0, 0, w, h);

        // Остановка цикла, если нет активных частиц
        if (particles.length === 0) {
            cancelAnimationFrame(animationFrame);
            animationFrame = null;
            lastTime = 0;
            return;
        }

        // Собираем только те ресурсы, которые реально рисуются в этом кадре
        const activeResources = new Set();

        particles = particles.filter(p => {
            const alive = p.update(w, h, deltaTime);
            if (alive) {
                activeResources.add(p.res);
                p.draw(ctx);
            }
            return alive;
        });

        // Обновляем кадр (WASM render) только для видимых стикеров и только 1 раз
        activeResources.forEach(res => res.advance(deltaTime));

        animationFrame = requestAnimationFrame(mainLoop);
    };

    const explode = async (x, y, params = {}) => {
        const config = {
            urls: params.stickers || [],
            speed: params.speed || 5,
            count: params.count || 8,
            type: params.type || 'burst'
        };

        if (!config.urls?.length) {
            return;
        }

        const resources = await tgs.prepareResources(config.urls, 180);

        const waves = config.type === 'burst' ? 3 : 1;
        
        for (let w = 0; w < waves; w++) {
            setTimeout(() => {
                for (let i = 0; i < config.count / waves; i++) {
                    const res = resources[Math.floor(Math.random() * resources.length)];
                    particles.push(tgs.spawnParticle(res, x, y, config.speed, config.type));
                }
                
                // Запуск цикла, если он простаивал
                if (!animationFrame && particles.length > 0) {
                    lastTime = performance.now();
                    animationFrame = requestAnimationFrame(mainLoop);
                }
            }, w * 150);
        }
    };

    const setupCanvas = () => {
        const canvas = canvasRef.value;
        if (!canvas) {
            return;
        }

        canvas.width = window.innerWidth * tgs.dpr;
        canvas.height = window.innerHeight * tgs.dpr;
        ctx = canvas.getContext('2d');
        ctx.scale(tgs.dpr, tgs.dpr);
    };

    onMounted(async () => {
        setupCanvas();
        window.addEventListener('resize', setupCanvas);
        await tgs.init();
        tgs.setExplode(explode);
    });

    onUnmounted(() => {
        window.removeEventListener('resize', setupCanvas);

        if (animationFrame) {
            cancelAnimationFrame(animationFrame);
        }

        tgs.clearCache();
    });
</script>

<style scoped>
.canvas {
    position: fixed;
    top: 0; left: 0;
    width: 100vw; height: 100vh;
    pointer-events: none;
    z-index: 9999;
}
</style>
